import { useCallback, type PropsWithChildren, type ReactNode } from 'react'
import { ScrollView, View } from 'react-native'

import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { DONATION_SOURCE } from '@/src/features/donation/api/types/donation'
import { ScrollListenerProvider } from '@/src/general/components/ScrollListenerProvider'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useMenu } from '@/src/general/hooks/useMenu'
import { useTranslate } from '@/src/lang/useTranslate'

import { Button } from '../button/default'
import { DonationButton } from '../button/donation/DonationButton'
import { Header, type THeaderProps } from '../header/Header'

type TCommonHeaderProps = {
  /**
   * This option can be enabled to highlight the burger menu button
   * when the background is colored ( eg : an image )
   */
  contrast?: boolean
} & THeaderProps

export const CommonHeader = ({ contrast, ...headerProps }: TCommonHeaderProps): ReactNode => {
  const { openMenu } = useMenu()
  const { xs } = useDimensions()

  const { initializeRef } = useAccessibilityContext()
  const { t } = useTranslate()

  const setMenuRef = useCallback(
    (ref: View | null) => {
      initializeRef(ref, 'menu')
    },
    [initializeRef],
  )

  return (
    <Header
      testID={TEST_ID.LAYOUT.COMMON}
      leftButtons={[
        xs && contrast
          ? {
              type: 'button',
              component: () => (
                <Button
                  isIconOnly
                  startIcon={ICON.BURGER_MENU}
                  onPress={openMenu}
                  testID={TEST_ID.NAVIGATION.BURGER_MENU()}
                  colors="primary"
                  variant="faded"
                  aria-label={t('app.how-to.open-menu')}
                  ref={setMenuRef}
                />
              ),
            }
          : {
              startIcon: ICON.BURGER_MENU,
              onPress: openMenu,
              type: 'icon',
              testID: TEST_ID.NAVIGATION.BURGER_MENU(),
              'aria-label': t('app.how-to.open-menu'),
              ref: setMenuRef,
            },
      ]}
      rightButtons={[
        {
          type: 'button',
          component: () => <DonationButton donationSource={DONATION_SOURCE.HEADER} isIconOnly={xs} />,
        },
      ]}
      showTabs
      {...headerProps}
    />
  )
}

export const CommonLayout = ({ children, testID }: PropsWithChildren<{ testID?: string }>): ReactNode => {
  return (
    <ScrollListenerProvider>
      {(scrollListenerProps) => (
        <ScrollView {...scrollListenerProps} className="h-screen w-screen pb-36" testID={testID}>
          <CommonHeader />
          <View className="web:flex-column native:flex-1 web:mx-auto web:flex web:w-full" role="main">
            {children}
          </View>
        </ScrollView>
      )}
    </ScrollListenerProvider>
  )
}
